import { yupResolver } from '@hookform/resolvers/yup';
import {
	Button,
	Collapse,
	Form,
	Input,
	Modal,
	Select,
	Space,
	Spin,
} from 'antd';
import {
	GetAllProjectsQuerySortField,
	ProjectDto,
	ProjectInvitationDto,
	SortOrder,
} from 'api';
import FormItem from 'components/form/FormItem';
import OmniNotification from 'components/OmniNotification';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TFunction, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import {
	createProject,
	getProjects,
	updateSorterIndex,
} from 'redux/reducers/projectsSlice';
import { AppDispatch } from 'redux/store';
import * as yup from 'yup';
import ProjectTile from 'components/Projects/ProjectTile/ProjectTile';
import styles from './ProjectsPage.module.less';
import { Content, Footer } from 'antd/lib/layout/layout';
import { getUserInfo } from 'redux/reducers/authenticationSlice';
import { getInvitationsForUser } from 'redux/reducers/collaboratorSlice';
import { userSelector } from 'redux/selectors/userSelector';
import { projectSorterIndexSelector } from 'redux/selectors/projectSelector';

import InvitationTile from 'components/Projects/InvitationTile/InvitationTile';
import cx from 'classnames';

const { Panel } = Collapse;

interface FormValues {
	name: string;
}

const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		name: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('ProjectSetupPage.Name'),
				}),
			)
			.max(
				50,
				t('validation.maxLength', {
					fieldName: t('ProjectSetupPage.Name'),
					count: 50,
				}),
			),
	});
export default function ProjectsPage() {
	const [userHasProjects, setUserHasProjects] = useState(false);
	const [createModalVisible, setCreateModalVisible] = useState(false);
	const [userProjects, setUserProjects] = useState<ProjectDto[]>();
	const [userHasInvitations, setUserHasInvitations] = useState(false);
	const [userInvitations, setUserInvitations] =
		useState<ProjectInvitationDto[]>();
	const [isLoading, setIsLoading] = useState(true);
	const { t } = useTranslation();
	const dispatch: AppDispatch = useDispatch();
	const validationSchema = useMemo(() => getValidationSchema(t), [t]);
	const userData = useSelector(userSelector);
	const sorterIndex = useSelector(projectSorterIndexSelector);

	useEffect(() => {
		loadProjects();
		loadInvitations();
	}, []);

	useEffect(() => {
		loadProjects();
	}, [sorterIndex]);

	const handleChange = useCallback(async () => {
		await dispatch(getUserInfo());
		await loadProjects();
		await loadInvitations();
	}, []);

	const loadProjects = async () => {
		setIsLoading(true);
		const sorter = sorters[sorterIndex];
		const result = await dispatch(getProjects(sorter));

		if (!getProjects.fulfilled.match(result)) {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: result.error.message || t('api.defaultError'),
			});
			return;
		}

		setUserHasProjects(!!result.payload.totalCount);
		setUserProjects(result.payload.items);

		if (!!result.payload.totalCount && !userData?.projectPermissions?.length) {
			dispatch(getUserInfo());
		}

		setIsLoading(false);
	};

	const loadInvitations = async () => {
		const result = await dispatch(getInvitationsForUser(userData?.email));

		if (!getInvitationsForUser.fulfilled.match(result)) {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: result.error.message || t('api.defaultError'),
			});
			return;
		}

		setUserHasInvitations(!!result.payload.length);
		setUserInvitations(result.payload);
	};

	const showCreateModal = () => {
		setCreateModalVisible(true);
	};

	const hideCreateModal = () => {
		setCreateModalVisible(false);
	};
	const { control, handleSubmit, formState, reset } = useForm<FormValues>({
		mode: 'all',
		criteriaMode: 'all',
		resolver: yupResolver(validationSchema),
		defaultValues: {
			name: '',
		},
	});

	const { isValid } = formState;

	const onSubmit = async (values: FormValues) => {
		const { name } = values;
		const data = {
			name,
		};

		const result = await dispatch(createProject(data.name));
		if (createProject.fulfilled.match(result)) {
			OmniNotification.success({
				message: t('ProjectPage.Notification.Header'),
				description: t('ProjectPage.Notification.Message', {
					project_name: name,
				}),
			});
			reset();
			handleChange();
		}
	};
	const getProjectTiles = () => {
		return userProjects?.map((userProject) => {
			return <ProjectTile project={userProject} key={userProject.id} />;
		});
	};

	const getUserInvitations = () => {
		return userInvitations?.map((invitation) => (
			<InvitationTile
				invitation={invitation}
				callback={handleChange}
				key={invitation.invitationId}
			/>
		));
	};

	const sorters = [
		{
			key: 0,
			label: t('ProjectPage.Sorter.NewestToOldest'),
			sortField: GetAllProjectsQuerySortField.CreatedDate,
			sortOrder: SortOrder.DESC,
		},
		{
			key: 1,
			label: t('ProjectPage.Sorter.OldestToNewest'),
			sortField: GetAllProjectsQuerySortField.CreatedDate,
			sortOrder: SortOrder.ASC,
		},
		{
			key: 2,
			label: t('ProjectPage.Sorter.AToZ'),
			sortField: GetAllProjectsQuerySortField.Name,
			sortOrder: SortOrder.ASC,
		},
		{
			key: 3,
			label: t('ProjectPage.Sorter.ZToA'),
			sortField: GetAllProjectsQuerySortField.Name,
			sortOrder: SortOrder.DESC,
		},
	];

	return (
		<>
			{!isLoading && !userHasProjects && <Redirect to="/projectSetup" />}
			<Content className={styles.projects}>
				<div className={cx(styles.projectsHeader, 'page-head')}>
					<Space>
						<h1>{t('ProjectsPage.Header')}</h1>
						<Button
							data-testid="create-btn"
							type="primary"
							htmlType="submit"
							size="large"
							onClick={showCreateModal}
						>
							{t('ProjectSetupPage.Button')}
						</Button>
					</Space>
					<Space>
						<Select
							style={{ width: 200 }}
							defaultValue={sorterIndex}
							onChange={(value) => {
								dispatch(updateSorterIndex(value));
							}}
						>
							{sorters.map((item) => (
								<Select.Option key={item.key} value={item.key}>
									{`${t('ProjectPage.Sorter.SortBy')} ${item.label}`}
								</Select.Option>
							))}
						</Select>
					</Space>
				</div>
				{isLoading ? (
					<Spin />
				) : (
					<Space size={[75, 50]} wrap className={styles.projectList}>
						{getProjectTiles()}
					</Space>
				)}
			</Content>
			{userHasInvitations && (
				<Footer className={styles.footer}>
					<>
						<Collapse
							defaultActiveKey={['1']}
							bordered={false}
							className={styles.invitations}
							ghost
						>
							<Panel
								header={
									<h1>
										{t('ProjectsPage.Invitations', {
											count: userInvitations?.length,
										})}
									</h1>
								}
								key="1"
							>
								<Space size={[75, 50]} wrap className={styles.projectList}>
									{getUserInvitations()}
								</Space>
							</Panel>
						</Collapse>
					</>
				</Footer>
			)}
			<Modal
				data-testid="create-project-modal"
				centered={true}
				title={t('ProjectSetupPage.Button')}
				visible={createModalVisible}
				onOk={() => {
					hideCreateModal();
					handleSubmit(onSubmit)();
				}}
				onCancel={() => {
					hideCreateModal();
					reset();
				}}
				okText={t('ProjectSetupPage.Button')}
				okButtonProps={{ disabled: !isValid, htmlType: 'submit' }}
				cancelButtonProps={{ disabled: false, className: 'color-dark' }}
				className={styles.createModal}
				maskClosable={false}
				keyboard={false}
			>
				<Form
					data-testid="create-new-project-form"
					layout="vertical"
					onFinish={handleSubmit(onSubmit)}
				>
					<FormItem
						name="name"
						label={t('ProjectSetupPage.Name')}
						control={control}
						render={({ field }) => (
							<Input
								data-testid="project-name-input"
								placeholder={t('ProjectsPage.InputPlaceholder')}
								size="large"
								{...field}
							/>
						)}
					/>
				</Form>
			</Modal>
		</>
	);
}
